import { ApiError } from '@packages/api';
import type { AnonymousUiUsageEventFields } from '@packages/types';

type FormFailed = AnonymousUiUsageEventFields<'ui-usage.anonymous.form.failed'>;

/**
 * Whether a failure says nothing about the request itself: a 5xx, a response the app could not
 * use (status 0) or an error that is not an `ApiError` (no network, a timeout). Form-agnostic,
 * for call sites that only separate "the service was unavailable" from "it refused us".
 */
export function isUiUsageUnavailable(error: unknown): boolean {
  return !(error instanceof ApiError) || error.status === 0 || error.status >= 500;
}

/**
 * The closed reason a form request failed, for `form.failed`. A 409 means "email taken" only on
 * signup; elsewhere it is a plain refusal. A response the app could not use (status 0) and any
 * error that is not an `ApiError` (no network, a timeout) are `unavailable`, like a 5xx.
 */
export function uiUsageFailureReason(error: unknown, form: FormFailed['form']): FormFailed['reason'] {
  // The instanceof check narrows `error` for the status tests below; `isUiUsageUnavailable` is the rule.
  if (isUiUsageUnavailable(error) || !(error instanceof ApiError)) return 'unavailable';
  if (error.status === 409 && form === 'signup') return 'email-taken';
  if (error.status === 429) return 'rate-limited';
  return 'rejected';
}
